/**
 * components/branch/HeadOfficeOnly.js
 *
 * What a team member limited to some branches sees on a page that belongs to
 * head office (4 Oct 2026): the wallet and payouts, the team, the business
 * profile and settings, and customer chats. Their menu does not list these
 * pages; this covers a bookmark, a link someone shared, or a page left open
 * when the owner changed their access. A plain explanation, never a blank
 * screen and never an error code.
 *
 *   <HeadOfficeOnly />
 *   <HeadOfficeOnly lead="Customer chats belong to head office." />
 */
import React from "react";
import { useHistory } from "react-router-dom";
import { AppButton } from "../ui";
import { useBranchScope } from "../../context/BranchScopeContext.js";
import { joinBranchNames } from "../../utilities/vendorScope.js";
import "./branch.css";

export const HEAD_OFFICE_LEAD = "This page belongs to head office.";
export const HEAD_OFFICE_ASK = "If you need it, ask the owner of the business to change your access.";

/**
 * The pages layouts/Admin.js holds back from a branch member because they are
 * head office's: every leaf marked `wholeBusinessOnly` in routes.js, or inside
 * a group that is. Their role does not matter here; the answer is head office
 * either way. Admin.js registers these paths to render this page.
 */
export const headOfficeOnlyRoutes = (routes, inherited = false) =>
  (routes || []).flatMap((route) => {
    const headOffice = inherited || Boolean(route.wholeBusinessOnly);
    if (route.collapse && route.views) return headOfficeOnlyRoutes(route.views, headOffice);
    return headOffice && route.layout === "/admin" && route.path ? [route] : [];
  });

/** "Your access covers Ntinda only." from the branches this login may see. */
export const coverageSentence = (names) => {
  const joined = joinBranchNames(names);
  return joined ? `Your access covers ${joined} only.` : "Your access covers some branches only.";
};

export default function HeadOfficeOnly({ lead = HEAD_OFFICE_LEAD, homePath = "/admin/dashboard" }) {
  const history = useHistory();
  const { locations } = useBranchScope();
  const names = (locations || []).map((l) => l.name).filter(Boolean);
  return (
    <div className="lk-hq-only" role="status">
      <i className="fa fa-store lk-hq-only__icon" aria-hidden="true" />
      <h4 className="lk-hq-only__title">Head office only</h4>
      <p className="lk-hq-only__text">
        {lead} {coverageSentence(names)}
      </p>
      <p className="lk-hq-only__text">{HEAD_OFFICE_ASK}</p>
      {history && (
        <AppButton variant="outline" onClick={() => history.push(homePath)}>
          Go to my dashboard
        </AppButton>
      )}
    </div>
  );
}
